<script setup>
import { ref, watch, computed } from 'vue'
import { useCmsContentStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'
import { storeToRefs } from 'pinia'

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth(700)

const cmsStore = useCmsContentStore()
const { inlineEditKey } = storeToRefs(cmsStore)

const contentItem = ref(null)
const editValue = ref('')
const fetching = ref(false)
const saving = ref(false)

const isDialogVisible = computed(() => inlineEditKey.value !== null)

const isText = computed(() => {
  if (!contentItem.value) {
    return true
  }

  return !contentItem.value.contentType || contentItem.value.contentType === 'text'
})

const isRichText = computed(() => contentItem.value?.contentType === 'richtext')
const isBlock = computed(() => contentItem.value?.contentType === 'block')

// TipTap config (matches existing CMS admin dialogs)
const tiptapOutput = 'html'
const tiptapOutlined = true
const tiptapMaxWidth = 900

// Fetch the content item when the dialog opens
watch(inlineEditKey, async key => {
  if (!key) {
    contentItem.value = null
    editValue.value = ''

    return
  }

  fetching.value = true
  try {
    const item = await cmsStore.getContentItemByKey(key)
    contentItem.value = item
    editValue.value = item?.value || ''
  } finally {
    fetching.value = false
  }
})

async function onSave() {
  if (!contentItem.value?._id) return

  saving.value = true
  try {
    await cmsStore.updateContentItem(contentItem.value._id, {
      value: editValue.value,
    })
    await cmsStore.reloadContent()
    cmsStore.closeInlineEdit()
  } catch (error) {
    console.error('Inline CMS save failed:', error)
  } finally {
    saving.value = false
  }
}

function onCancel() {
  cmsStore.closeInlineEdit()
}
</script>

<template>
  <VDialog
    :model-value="isDialogVisible"
    :width="dWidth"
    @update:model-value="val => { if (!val) onCancel() }"
  >
    <VCard class="pa-sm-9 pa-5 pt-4">
      <VCardItem class="text-center">
        <VCardTitle class="text-h5">
          Edit Content
        </VCardTitle>
        <VCardSubtitle class="mt-4">
          <code>{{ inlineEditKey }}</code>
        </VCardSubtitle>
      </VCardItem>

      <VCardText>
        <!-- Loading state -->
        <div
          v-if="fetching"
          class="d-flex justify-center py-8"
        >
          <VProgressCircular indeterminate />
        </div>

        <!-- Item not found -->
        <VAlert
          v-else-if="!contentItem"
          type="warning"
          class="mb-4"
        >
          Content item not found in CMS for key "{{ inlineEditKey }}".
          This content may only exist in the static translation file.
        </VAlert>

        <!-- Block type — redirect to admin page -->
        <VAlert
          v-else-if="isBlock"
          type="info"
          class="mb-4 mt-4"
        >
          Block content must be edited from the
          <RouterLink to="/all-cms-content">
            CMS admin page
          </RouterLink>.
        </VAlert>

        <!-- Text / Richtext editor -->
        <template v-else>
          <!-- Plain text -->
          <VTextarea
            v-if="isText"
            v-model="editValue"
            class="mt-4"
            label="Content"
            rows="3"
            auto-grow
          />

          <!-- Rich text -->
          <div v-if="isRichText">
            <div class="text-body-2 mb-1">
              Content (Rich Text)
            </div>
            <VuetifyTiptap
              v-model="editValue"
              label="Content"
              :output="tiptapOutput"
              :outlined="tiptapOutlined"
              rounded
              :max-height="400"
              :max-width="tiptapMaxWidth"
            />
          </div>

          <div class="d-flex flex-wrap justify-center gap-4 mt-6">
            <VBtn
              :loading="saving"
              color="primary"
              @click="onSave"
            >
              Save
            </VBtn>
            <VBtn
              color="secondary"
              variant="tonal"
              @click="onCancel"
            >
              Cancel
            </VBtn>
          </div>
        </template>
      </VCardText>
    </VCard>
  </VDialog>
</template>
